import Layout from "components/layout";
import Filters from "components/filters";
import Lesson from "components/pages/lessons/Lesson";
import CurrentFilters from "components/current_filters";
import Loader from "components/loader";
import InfiniteScrollable from "components/behaviors/infinite_scrollable";

import * as legacyLessonsApi from "api/legacy_lessons";
import * as legacyTopicsApi from "api/legacy_topics";
import { lessons as lessonsFixtures } from "components/pages/lessons/fixtures";
import { isMobile, params, resetScroll } from "utils/browser";
import { normalizeFilters } from "utils/metadata";
import { track } from "utils/tracker";
import { lessonsPath } from "utils/lessons";
import { BsFilter } from "react-icons/bs";
import { filterObjByPresent } from "utils";

import { useState, useEffect } from "react";
import { useMounted } from "hooks/react";
import { useUser } from "hooks/user";
import { useRouter } from "next/router";

export async function getServerSideProps({ res, query }) {
  res.setHeader(
    "Cache-Control",
    "public, max-age=3600, stale-while-revalidate=86400"
  );

  const { disciplina, topico } = query;

  return {
    props: filterObjByPresent({ subject: disciplina, topic: topico })
  };
}

interface LessonsProps {
  subject: string;
  topic: string;
}

export default function Lessons(props: LessonsProps): JSX.Element {
  const { subject: querySubject, topic: queryTopic } = props;

  const [lessons, setLessons] = useState(lessonsFixtures);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [loadedAllPages, setLoadedAllPages] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const router = useRouter();
  const { user } = useUser();

  useMounted(handleMount);

  useEffect(() => {
    if (!loading || page === 1) return;

    document
      .querySelector(".lessons")
      .scrollIntoView({ behavior: "smooth", block: "end" });
  }, [loading, page]);

  function handleMount() {
    resetScroll();
    filtersFromUrl().then((urlFilters) => {
      fetchLessons(1, urlFilters);
    });
  }

  function handlePagination(_event) {
    fetchLessons(page + 1);
  }

  async function fetchLessons(newPage = null, newFilters = null) {
    setLoading(true);

    if (newFilters && JSON.stringify(newFilters) !== "{}") {
      setFilters(newFilters);
      router.push(lessonsPath(newFilters));
    }
    if (newPage) setPage(newPage);

    newPage = newPage || page;
    legacyLessonsApi.index(newPage, newFilters || filters).then((response) => {
      const newLessons = response.body;
      if (newLessons.length === 0) {
        if (newPage === 1) handleNotFound(newFilters || filters);
        setLoading(false);
        return setLoadedAllPages(true);
      }

      const baseLessons = newPage === 1 ? [] : lessons;
      setLessons([...baseLessons, ...newLessons]);
      setLoading(false);
    });
  }

  function handleSearch(newFilters) {
    track("Searched lessons", normalizeFilters(newFilters));
    setLoadedAllPages(false);
    document.querySelector("body").scrollTop = 0;
    setLoading(true);
    setLessons(lessonsFixtures);
    fetchLessons(1, newFilters);
    if (isMobile()) setShowFilters(false);
  }

  function handleNotFound(filters) {
    track("Did not find lessons", normalizeFilters(filters));
    setLessons([]);
  }

  function updateLesson(lessonId, attributes) {
    const newLessons = lessons.map((lesson: any) => {
      if (lesson.id != lessonId) return lesson;

      return { ...lesson, ...attributes };
    });
    setLessons(newLessons);
  }

  function filtersFromUrl() {
    return filtersFromSlugs(params().disciplina, params().topico);
  }

  function filtersFromSlugs(subjectSlug: string, topicSlug: string) {
    if (!subjectSlug) return new Promise((resolve) => resolve({}));

    return legacyTopicsApi.get(subjectSlug).then((response) => {
      const data = response.body;
      const filters = { subject: { name: data.name, slug: data.slug } };
      if (!topicSlug) return filters;

      const topicInfo = data.children.find((child) => child.slug == topicSlug);
      if (!topicInfo) return filters;
      return {
        ...filters,
        topic: { name: topicInfo.name, slug: topicInfo.slug }
      };
    });
  }

  function canonicalUrl() {
    return lessonsPath({
      subject: querySubject && { slug: querySubject },
      topic: queryTopic && { slug: queryTopic }
    });
  }

  return (
    <Layout
      title="Aulas do ENEM e vestibulares"
      description="A nossa curadoria de aulas do YouTube categorizadas por matéria. O recurso que estava faltando para os seus estudos do ENEM e vestibulares."
      canonical={canonicalUrl()}
    >
      <main className="!pb-20 md:!p-0">
        <div className="flex">
          <div className="hidden-xs w-[300px] sticky z-10 top-0 flex-shrink-0 h-screen shadow-md">
            <Filters
              onSearch={handleSearch}
              loading={loading && page === 1}
              actionText="Buscar aulas"
              className="h-full"
              onClose={() => setShowFilters(false)}
            />
          </div>

          {showFilters && (
            <div className="visible-xs fixed z-40 left-0 top-0 w-full h-full">
              <Filters
                onSearch={handleSearch}
                loading={loading && page === 1}
                actionText="Buscar aulas"
                isClosable={true}
                className="h-full"
                onClose={() => setShowFilters(false)}
              />
            </div>
          )}
          <div className="container md:p-5">
            <div className="mb-5">
              <CurrentFilters
                filters={normalizeFilters(filters)}
                resourcesName="Aulas"
                loading={loading && page == 1}
                onClick={() => setShowFilters(true)}
              />
              <div
                className="flex items-center mt-2 md:hidden"
                onClick={() => setShowFilters(true)}
              >
                <BsFilter size={16} />
                <span className="ml-1 text-sm font-semibold">
                  Filtrar aulas
                </span>
              </div>
            </div>
            <div className="lessons">
              <InfiniteScrollable
                onPaginate={handlePagination}
                loading={loading}
                skipPagination={loadedAllPages}
              >
                {lessons.map((object: any) => {
                  return (
                    <div key={object.id} className="mb-4">
                      <Lesson
                        {...object}
                        user={user}
                        onChange={(attributes) =>
                          updateLesson(object.id, attributes)
                        }
                        loading={loading && page == 1}
                      />
                    </div>
                  );
                })}
              </InfiniteScrollable>
            </div>

            {loading && page > 1 && (
              <div className="flex justify-center mb-2 mt-8">
                <Loader />
              </div>
            )}

            {loadedAllPages && (
              <div className="flex justify-center mt-8">
                {(page > 1 && <span>Todas as aulas foram carregadas</span>) || (
                  <span>
                    Não encontramos aulas com os filtros selecionados :(
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </Layout>
  );
}
